import React, { useState, useEffect } from 'react';
import { CourseCard as UICourseCard } from '@anyemp/ui-kit';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { coursesAPI } from '../services/api';

const PLACEHOLDER_IMAGE = '/default_course.svg';

const CourseCard = ({ course, progress, isNext = false, sx }) => {
  const { t } = useTranslation(['courses']);
  const { isAuthenticated } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(!!course.bookmarked);
  const serverUrl = process.env.REACT_APP_APP_URL;

  const getRawImageUrl = () => {
    if (!course.image) return null;
    return course.image.startsWith('http')
      ? course.image
      : `${serverUrl}${course.image}`;
  };

  const [imageUrl, setImageUrl] = useState(() => getRawImageUrl() || PLACEHOLDER_IMAGE);

  useEffect(() => {
    const src = getRawImageUrl();
    if (!src) {
      setImageUrl(PLACEHOLDER_IMAGE);
      return;
    }
    setImageUrl(src);
    const img = new Image();
    img.onerror = () => {
      console.error(`Image load error for course ${course._id}:`, course.image);
      setImageUrl(PLACEHOLDER_IMAGE);
    };
    img.src = src;
  }, [course.image, course._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBookmarkToggle = async () => {
    try {
      const response = await coursesAPI.toggleBookmark(course._id);
      setIsBookmarked(response.data.bookmarked);
      toast.success(
        response.data.bookmarked
          ? t('courses:courseDetail.toasts.bookmarkAdded')
          : t('courses:courseDetail.toasts.bookmarkRemoved')
      );
    } catch (e) {
      toast.error(t('courses:courseDetail.toasts.bookmarkError'));
    }
  };

  const progressValue =
    typeof progress === 'number'
      ? progress
      : course.userProgress?.isEnrolled
      ? course.userProgress.progress
      : undefined;

  const isCompleted = !!course.userProgress?.completed || progressValue === 100;

  const instructor = course.instructor;
  const instructorName = typeof instructor === 'object' && instructor !== null
    ? (instructor.username || instructor.userMeta?.fullName)
    : undefined;
  const author = instructorName
    ? {
        username: instructorName,
        avatarUrl: instructor.userMeta?.avatar
          ? (instructor.userMeta.avatar.startsWith('http')
              ? instructor.userMeta.avatar
              : `${serverUrl}${instructor.userMeta.avatar}`)
          : undefined,
      }
    : undefined;

  return (
    <UICourseCard
      title={course.title}
      description={course.description}
      imageUrl={imageUrl}
      difficulty={course.difficulty?.toLowerCase()}
      durationMinutes={course.duration}
      lessonCount={course.lessonCount}
      testCount={course.testCount}
      videoCount={Array.isArray(course.videoContent) ? course.videoContent.length : 0}
      presentationCount={course.presentationCount || 0}
      professions={course.professions}
      progress={progressValue}
      rating={course.rating > 0 ? course.rating : undefined}
      author={author}
      isCompleted={isCompleted}
      isDraft={!!course.isDraft}
      isRecommended={isNext}
      isBookmarked={isAuthenticated && typeof course.bookmarked !== 'undefined' ? isBookmarked : undefined}
      onBookmarkToggle={isAuthenticated && typeof course.bookmarked !== 'undefined' ? handleBookmarkToggle : undefined}
      href={`/courses/${course.slug || course._id}`}
      sx={sx}
    />
  );
};

export default CourseCard;
